import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DEFAULT_NOTIFICATION_PREFS } from '@rw/shared';
import type { MeResponse, NotificationCategory, UpdateSettingsInput } from '@rw/shared';
import { useEffect, useState } from 'react';
import { Button, Card } from '../../components/ui';
import { ApiError, api } from '../../lib/api';
import { disablePush, enablePush, pushState, type PushState } from '../../lib/push';
import { useAuth } from '../../stores/auth';
import { useNetwork } from '../../stores/network';
import { toast } from '../../stores/toasts';
import { Toggle } from './SettingsCard';
import { useT } from '../../i18n';

const CATEGORIES: Array<{ key: NotificationCategory; label: string; description: string }> = [
  { key: 'territory', label: 'Territory lost', description: 'A rival ran through your sectors.' },
  { key: 'social', label: 'Kudos & comments', description: 'Someone cheered or commented on your run.' },
  { key: 'events', label: 'Events', description: 'Invitations, results and changes to events you’re in.' },
  { key: 'clubs', label: 'Club', description: 'Invites, join requests and members.' },
  { key: 'imports', label: 'Strava imports', description: 'A run arrived from Strava.' },
  { key: 'achievements', label: 'Ranks & badges', description: 'You reached a new rank or unlocked a badge.' },
];

const HINT: Partial<Record<PushState, string>> = {
  'needs-install':
    'On iPhone, notifications work in the installed app: tap Share → “Add to Home Screen”, open Runner Wars from your home screen, then turn them on here.',
  unsupported: 'This browser doesn’t support push notifications.',
  disabled: 'Push notifications aren’t available on this server yet.',
  denied: 'Notifications are blocked for Runner Wars. Allow them in your phone or browser settings, then come back.',
};

export function NotificationsCard() {
  const user = useAuth((s) => s.user);
  const setUser = useAuth((s) => s.setUser);
  const online = useNetwork((s) => s.online);
  const qc = useQueryClient();
  const [state, setState] = useState<PushState | null>(null);
  const [busy, setBusy] = useState(false);
  const [asking, setAsking] = useState(false);
  const t = useT();

  useEffect(() => {
    let alive = true;
    void pushState()
      .then((s) => alive && setState(s))
      .catch(() => alive && setState('off'));
    return () => {
      alive = false;
    };
  }, []);

  async function toggleDevice(on: boolean) {
    setBusy(true);
    try {
      setState(on ? await enablePush(() => setAsking(true)) : await disablePush());
    } catch (e) {
      toast({ kind: 'error', title: t('Could not change notifications'), body: e instanceof Error ? e.message : undefined });
    } finally {
      setBusy(false);
      setAsking(false);
    }
  }

  const update = useMutation({
    mutationFn: (body: UpdateSettingsInput) => api<MeResponse>('/me/settings', { method: 'PATCH', body }),
    onSuccess: (u) => {
      qc.setQueryData(['me', 'profile'], u);
      setUser(u);
    },
    onError: (e) => toast({ kind: 'error', title: t('Could not save setting'), body: e instanceof ApiError ? e.message : undefined }),
  });

  const prefs = { ...DEFAULT_NOTIFICATION_PREFS, ...user?.settings?.notifications, ...update.variables?.notifications };
  const hint = state && HINT[state] ? t(HINT[state]) : undefined;

  return (
    <Card>
      <div className="flex items-center gap-4">
        <div className="flex-1">
          <div className="font-semibold">{t('Push notifications')}</div>
          <p className="mt-0.5 text-xs text-ink-400">
            {t(state === 'on' ? 'On for this device. You’ll be notified when the app is closed.' : 'Get notified on this device even when the app is closed.')}
          </p>
        </div>
        {state === 'on' ? (
          <Button size="sm" variant="ghost" loading={busy} disabled={busy} onClick={() => void toggleDevice(false)} data-testid="push-disable">
            {t('Turn off')}
          </Button>
        ) : state === 'off' ? (
          <Button size="sm" loading={busy} disabled={busy || !online} onClick={() => void toggleDevice(true)} data-testid="push-enable">
            {t('Turn on')}
          </Button>
        ) : null}
      </div>
      {asking ? (
        <p className="mt-3 rounded-lg bg-ink-800 p-3 text-xs text-ink-300" data-testid="push-asking">
          {t('Choose “Allow” in the notification prompt. On a Mac it can appear as a banner in the top-right corner — hover it, then Options → Allow.')}
        </p>
      ) : null}
      {hint ? (
        <p className="mt-3 rounded-lg bg-ink-800 p-3 text-xs text-ink-300" data-testid="push-hint">
          {hint}
        </p>
      ) : null}
      {state === 'on' ? (
        <div className="mt-4 space-y-4 border-t border-ink-700 pt-4">
          {CATEGORIES.map((c) => (
            <Toggle
              key={c.key}
              id={`notify-${c.key}`}
              checked={prefs[c.key]}
              disabled={!online || !user?.settings}
              onChange={(v) => update.mutate({ notifications: { [c.key]: v } })}
              label={t(c.label)}
              description={t(c.description)}
            />
          ))}
        </div>
      ) : null}
    </Card>
  );
}
